import { CannedSegmentCreator } from '@fullstory/client-api';
import { CannedMetricCreator } from '@fullstory/client-api/goimports/fs_services_webber_internal_saved_metrics';
import { Box, ButtonBase, Flex, Text, Tooltip } from '@fullstory/components';
import { intl } from '@fullstory/intl';
import { AuditLog } from '@fullstory/junk-drawer-ui';
import { Timestamp, timestamp } from '@fullstory/proto/google/protobuf';
import { LibraryObjectType } from '@fullstory/routes';
import React, { useMemo, useState } from 'react';
import { libraryObjectTypeMessages } from '../../../constants';
import { RenameModal } from '../RenameModal';

export interface DxObjectTitleCreatorProps {
  title: string;
  ownedBy: string[];
  canUpdate: boolean;
  onRenameClick: (payload: { name: string; description: string }) => void;
  onOwnersClick?: () => void;
  description: string;
  createdBy: string;
  created: timestamp;
  lastUpdated: timestamp;
  lastUpdatedBy: string;
  objectType: LibraryObjectType;
}

export const DxObjectTitleCreator = ({
  title,
  ownedBy,
  canUpdate,
  onRenameClick,
  onOwnersClick,
  description,
  createdBy,
  created,
  lastUpdated,
  lastUpdatedBy,
  objectType,
}: DxObjectTitleCreatorProps) => {
  const [renameModalOpen, setRenameModalOpen] = useState<boolean>(false);
  const ownerLabel = useMemo(() => {
    if (ownedBy.length === 0) {
      return intl.formatMessage(
        { defaultMessage: 'Creator {creator}', id: 'N1grzg' },
        { creator: createdBy },
      );
    }

    return ownedBy.length === 1
      ? intl.formatMessage(
          {
            defaultMessage: 'Owner {ownedBy}',
            id: '3hSgJ7',
          },
          { ownedBy: ownedBy[0] },
        )
      : intl.formatMessage(
          {
            defaultMessage: 'Owner {ownedBy} and {count, plural, one {# other} other {# others}}',
            id: 'v88PVs',
          },
          { ownedBy: ownedBy[0], count: ownedBy.length - 1 },
        );
  }, [createdBy, ownedBy]);
  return (
    <>
      <Flex column alignItems="flex-start">
        <Box marginRight={16} className="fs_searchie_detail_back_button" />
        <ButtonBase
          disabled={!canUpdate}
          onClick={() => {
            setRenameModalOpen(true);
          }}
        >
          <Text tag="h1" appearance="bodyLargeBold" testId="name-label" align="left">
            {title}
          </Text>
        </ButtonBase>
        <Box marginRight={24}>
          <Tooltip
            placement="bottom-start"
            variant="light"
            maxWidth={429}
            disablePadding
            tooltipContent={
              createdBy.toLowerCase() !== CannedMetricCreator.toLowerCase() &&
              createdBy.toLowerCase() !== CannedSegmentCreator.toLowerCase() && (
                <AuditLog
                  createdBy={createdBy}
                  createdAt={Timestamp.mustDate(created)}
                  updatedBy={lastUpdatedBy}
                  updatedAt={Timestamp.mustDate(lastUpdated)}
                  width={425}
                  objectType={libraryObjectTypeMessages[objectType]}
                />
              )
            }
          >
            <ButtonBase disabled={!onOwnersClick || !canUpdate} onClick={onOwnersClick}>
              <Text appearance="bodySmall" testId="resource-creator-label">
                {ownerLabel}
              </Text>
            </ButtonBase>
          </Tooltip>
        </Box>
      </Flex>
      <RenameModal
        name={title}
        onCloseSignal={() => setRenameModalOpen(false)}
        type={objectType}
        description={description}
        onSubmit={(name: string, description: string) => {
          onRenameClick({ name, description });
          setRenameModalOpen(false);
        }}
        open={renameModalOpen}
      />
    </>
  );
};
